// src/componants/manager/PenaltyManager.js
import React, { useEffect, useMemo, useState } from "react";
import { useData } from "../../context/DataContext";
import { useAuth } from "../../context/AuthContext";
import {
  createPenalty,
  approvePenalty,
  rejectPenalty,
} from "../../api/penalties";
import { getActiveAttendanceRegulation } from "../../api/attendanceRegulations";
import { uploadAttachments } from "../../api/attachments";
import { createAppeal } from "../../api/appeals";
import {
  isHrFamilyRole,
  isCompanyWideViewerRole,
} from "../../utils/hrStaffHelpers";
import {
  filterHrPenaltyList,
  PENALTY_LIST_RECENT_MONTHS,
  splitPenaltiesByMonth,
} from "../../utils/penaltyMonthHelpers";
import { formatPenaltyDays } from "../../utils/penaltyFormat";
import { formatWorkplaceLabel } from "../../utils/dataMappers";
import AttachmentUploader from "../common/AttachmentUploader";
import AttachmentList from "../common/AttachmentList";
import AppealDeadlineClock from "../common/AppealDeadlineClock";
import "./PenaltyManager.css";

const penaltyTypes = [
  { value: "FINANCIAL_PENALTY", label: "جزاء مالي / خصم أيام" },
  { value: "WARNING", label: "إنذار" },
];

/** Quick presets for financial penalty days */
const DAY_PRESETS = [
  { value: 0.25, label: "ربع يوم" },
  { value: 0.5, label: "نص يوم" },
  { value: 1, label: "يوم" },
  { value: 2, label: "يومان" },
  { value: 3, label: "3 أيام" },
];

function PenaltyManager({ userRole }) {
  const { user } = useAuth();
  const {
    employeesByBranch,
    employeePenalties,
    teamMembers,
    refreshData,
    addRecentActivity,
    getManagedEmployees,
  } = useData();

  const [showAddModal, setShowAddModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [employeeSearch, setEmployeeSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [reviewModal, setReviewModal] = useState({
    open: false,
    type: "",
    penaltyId: null,
    notes: "",
  });
  const [reviewError, setReviewError] = useState("");
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [appealModal, setAppealModal] = useState({
    open: false,
    penaltyId: null,
    submitted: false,
  });
  const [appealReason, setAppealReason] = useState("");
  const [appealError, setAppealError] = useState("");
  const [appealSubmitting, setAppealSubmitting] = useState(false);

  const [newPenalty, setNewPenalty] = useState({
    employeeId: "",
    type: "",
    date: "",
    days: "",
    reason: "",
  });
  const [penaltyFiles, setPenaltyFiles] = useState([]);
  const [specialPenalties, setSpecialPenalties] = useState([]);

  useEffect(() => {
    let cancelled = false;
    getActiveAttendanceRegulation()
      .then((data) => {
        if (cancelled) return;
        const items = Array.isArray(data?.specialPenalties)
          ? data.specialPenalties
          : [];
        setSpecialPenalties(items);
      })
      .catch(() => {
        if (!cancelled) setSpecialPenalties([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const canReviewPenalties =
    isHrFamilyRole(userRole) || userRole === "gm" || userRole === "admin";
  const createsDirectly = canReviewPenalties;

  // Employee profile id — managerId / departmentManagerId on reports point here
  const currentEmployeeId = user?.employeeId || null;

  const usesTeamScope =
    userRole === "manager" || userRole === "department-manager";

  const managedEmployees = useMemo(
    () =>
      (
        usesTeamScope && teamMembers?.length
          ? teamMembers
          : getManagedEmployees(currentEmployeeId, userRole)
      ).filter(
        (e) =>
          e.status !== "terminated" &&
          String(e.id) !== String(currentEmployeeId),
      ),
    [
      usesTeamScope,
      userRole,
      teamMembers,
      getManagedEmployees,
      currentEmployeeId,
    ],
  );

  const managedEmployeeIds = useMemo(
    () => new Set(managedEmployees.map((e) => String(e.id))),
    [managedEmployees],
  );

  const filteredManagedEmployees = useMemo(() => {
    const q = employeeSearch.trim().toLowerCase();
    const matches = q
      ? managedEmployees.filter((emp) => {
          const haystack = [
            emp.name,
            emp.fullName,
            emp.code,
            emp.employeeCode,
            emp.jobTitle,
            emp.departmentName,
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();
          return haystack.includes(q);
        })
      : managedEmployees;
    return matches.slice(0, 60);
  }, [managedEmployees, employeeSearch]);

  // ✅ الجزاءات من الـ API (employeePenalties يشمل الكل ضمن صلاحية المستخدم)
  const allPenalties = employeePenalties;

  const employeeById = useMemo(() => {
    const map = new Map();
    for (const emp of Object.values(employeesByBranch || {}).flat()) {
      map.set(String(emp.id), emp);
    }
    return map;
  }, [employeesByBranch]);

  const myOwnPenalties = useMemo(() => {
    if (!currentEmployeeId || !isHrFamilyRole(userRole)) return [];
    return allPenalties.filter(
      (pen) =>
        String(pen.employeeId) === String(currentEmployeeId) &&
        (pen.status === "active" || pen.status === "appealed"),
    );
  }, [allPenalties, currentEmployeeId, userRole]);

  const myOwnPenaltyGroups = useMemo(
    () =>
      splitPenaltiesByMonth(myOwnPenalties, new Date(), {
        recentMonths: PENALTY_LIST_RECENT_MONTHS,
      }),
    [myOwnPenalties],
  );

  const isHrPenaltyView = isHrFamilyRole(userRole);

  // ✅ فلترة الجزاءات - كل مدير يشوف جزاءات اللي تحته لحد أقل رتبة
  const visiblePenalties = useMemo(() => {
    const companyWide =
      isHrFamilyRole(userRole) || isCompanyWideViewerRole(userRole);
    const scoped = allPenalties.filter((pen) => {
      if (companyWide) return true;
      if (managedEmployeeIds.has(String(pen.employeeId))) return true;
      if (employeeById.has(String(pen.employeeId))) return false;
      // Backend-scoped list — show if employee was returned by API even if
      // not in local org tree (e.g. after refresh lag).
      return Boolean(pen.employeeName);
    });
    return isHrFamilyRole(userRole) ? filterHrPenaltyList(scoped) : scoped;
  }, [allPenalties, userRole, managedEmployeeIds, employeeById]);

  const filteredPenalties = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return visiblePenalties.filter((p) => {
      // "الكل" excludes cancelled auto leftovers that dominate the list.
      if (filter === "all") {
        if (p.status === "cancelled") return false;
      } else if (p.status !== filter) {
        return false;
      }
      if (!q) return true;
      const employee = employeeById.get(String(p.employeeId));
      const haystack = [
        employee?.name,
        p.employeeName,
        employee?.code,
        p.employeeCode,
        p.type,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [visiblePenalties, filter, searchTerm, employeeById]);

  const pendingHrCount = useMemo(
    () => visiblePenalties.filter((p) => p.status === "pending_hr").length,
    [visiblePenalties],
  );

  const activeCount = useMemo(
    () => visiblePenalties.filter((p) => p.status === "active").length,
    [visiblePenalties],
  );

  const appealedCount = useMemo(
    () => visiblePenalties.filter((p) => p.status === "appealed").length,
    [visiblePenalties],
  );

  const listedCount = useMemo(
    () =>
      visiblePenalties.filter((p) => p.status !== "cancelled").length,
    [visiblePenalties],
  );

  // ✅ فتح مودال إضافة جزاء
  function openAddModal() {
    setNewPenalty({ employeeId: "", type: "", date: "", days: "", reason: "" });
    setPenaltyFiles([]);
    setSelectedEmployee(null);
    setEmployeeSearch("");
    setError("");
    setShowAddModal(true);
  }

  // ✅ إضافة الجزاء
  async function handleAddPenalty(e) {
    e.preventDefault();
    setError("");

    if (!newPenalty.employeeId) {
      setError("من فضلك اختر الموظف");
      return;
    }
    if (!newPenalty.type) {
      setError("من فضلك اختر نوع الجزاء");
      return;
    }
    if (!newPenalty.date) {
      setError("من فضلك أدخل تاريخ الجزاء");
      return;
    }
    if (
      newPenalty.type === "FINANCIAL_PENALTY" &&
      (!newPenalty.days || Number(newPenalty.days) < 0.25)
    ) {
      setError("من فضلك أدخل عدد أيام صحيح (ربع يوم فأكثر)");
      return;
    }
    if (!newPenalty.reason.trim()) {
      setError("من فضلك أدخل سبب الجزاء");
      return;
    }

    const employee = managedEmployees.find(
      (e) => String(e.id) === String(newPenalty.employeeId),
    );
    if (!employee) {
      setError("الموظف غير موجود أو لا تملك صلاحية إضافة جزاء له");
      return;
    }

    setSubmitting(true);
    try {
      const created = await createPenalty({
        employeeId: employee.id,
        type: newPenalty.type,
        days:
          newPenalty.type === "FINANCIAL_PENALTY"
            ? Number(newPenalty.days)
            : undefined,
        reason: newPenalty.reason.trim(),
        effectiveDate: newPenalty.date,
      });

      if (penaltyFiles.length && created?.id) {
        await uploadAttachments("penalty", created.id, penaltyFiles);
      }

      await refreshData();

      const typeLabel =
        penaltyTypes.find((t) => t.value === newPenalty.type)?.label ||
        newPenalty.type;

      addRecentActivity({
        id: Date.now(),
        text: createsDirectly
          ? `جزاء ${typeLabel} على ${employee.name}`
          : `جزاء ${typeLabel} على ${employee.name} — بانتظار اعتماد HR`,
        date: new Date().toLocaleDateString("ar-EG"),
        type: "appeal",
      });

      setShowAddModal(false);
      setNewPenalty({
        employeeId: "",
        type: "",
        date: "",
        days: "",
        reason: "",
      });
      setPenaltyFiles([]);
      setSelectedEmployee(null);
      setEmployeeSearch("");
    } catch (err) {
      const message =
        err.response?.data?.message ||
        err.response?.data?.error ||
        "تعذر تسجيل الجزاء";
      setError(Array.isArray(message) ? message.join("\n") : message);
    } finally {
      setSubmitting(false);
    }
  }

  function handleEmployeeSelect(employeeId) {
    const emp = managedEmployees.find((e) => String(e.id) === String(employeeId));
    setSelectedEmployee(emp || null);
    setNewPenalty((prev) => ({ ...prev, employeeId }));
  }

  function openReviewModal(penaltyId, type) {
    setReviewModal({ open: true, type, penaltyId, notes: "" });
    setReviewError("");
  }

  function closeReviewModal() {
    setReviewModal({ open: false, type: "", penaltyId: null, notes: "" });
    setReviewError("");
    setReviewSubmitting(false);
  }

  function openAppealModal(penaltyId) {
    setAppealModal({ open: true, penaltyId, submitted: false });
    setAppealReason("");
    setAppealError("");
  }

  function closeAppealModal() {
    setAppealModal({ open: false, penaltyId: null, submitted: false });
    setAppealReason("");
    setAppealError("");
    setAppealSubmitting(false);
  }

  async function handleAppealSubmit(e) {
    e.preventDefault();
    if (!appealReason.trim()) {
      setAppealError("يجب إدخال سبب التظلم");
      return;
    }
    setAppealSubmitting(true);
    setAppealError("");
    try {
      await createAppeal({
        type: "penalty",
        reason: appealReason.trim(),
        penaltyId: appealModal.penaltyId,
      });
      await refreshData();
      setAppealModal((prev) => ({ ...prev, submitted: true }));
    } catch (err) {
      const message =
        err.response?.data?.message ||
        err.response?.data?.error ||
        "تعذر إرسال التظلم";
      setAppealError(Array.isArray(message) ? message.join("\n") : message);
    } finally {
      setAppealSubmitting(false);
    }
  }

  async function handleConfirmReview() {
    const { type, notes, penaltyId } = reviewModal;
    if (type === "reject" && !notes.trim()) {
      setReviewError("يجب إدخال سبب الرفض");
      return;
    }

    setReviewSubmitting(true);
    setReviewError("");
    try {
      if (type === "approve") {
        await approvePenalty(penaltyId, {
          notes: notes.trim() || undefined,
        });
      } else {
        await rejectPenalty(penaltyId, { notes: notes.trim() });
      }
      closeReviewModal();
      await refreshData();
    } catch (err) {
      const message = err.response?.data?.message;
      setReviewError(
        Array.isArray(message)
          ? message.join("\n")
          : message || "تعذر تنفيذ الإجراء على الجزاء",
      );
      setReviewSubmitting(false);
    }
  }

  function getStatusBadge(pen) {
    const status = pen?.status;
    if (status === "pending_hr")
      return (
        <span className="penalty-status-badge pending">بانتظار اعتماد HR</span>
      );
    if (status === "appealed")
      return (
        <span className="penalty-status-badge review">
          {pen.appealStatus || "معلق"}
        </span>
      );
    if (status === "active")
      return <span className="penalty-status-badge active">ساري</span>;
    if (status === "rejected")
      return <span className="penalty-status-badge rejected">مرفوض</span>;
    return <span className="penalty-status-badge cancelled">ملغي</span>;
  }

  function findEmployee(employeeId) {
    return employeeById.get(String(employeeId));
  }

  function findPenaltyForEmployee(employeeId) {
    return allPenalties.find(
      (p) => String(p.employeeId) === String(employeeId),
    );
  }

  function getEmployeeName(employeeId) {
    const employee = findEmployee(employeeId);
    if (employee?.name) return employee.name;
    return findPenaltyForEmployee(employeeId)?.employeeName || "غير معروف";
  }

  function getEmployeeCode(employeeId) {
    const employee = findEmployee(employeeId);
    if (employee?.code) return employee.code;
    return findPenaltyForEmployee(employeeId)?.employeeCode || "";
  }

  function getEmployeeMeta(employeeId, pen) {
    const employee = findEmployee(employeeId);
    const fromPenalty = pen || findPenaltyForEmployee(employeeId);
    const jobTitle = employee?.jobTitle || fromPenalty?.jobTitle || "";
    const workplaceLabel =
      fromPenalty?.workplaceLabel ||
      formatWorkplaceLabel(
        fromPenalty?.branchName || employee?.branchName,
        fromPenalty?.departmentName || employee?.departmentName,
      );
    return { jobTitle, workplaceLabel };
  }

  function renderOwnPenaltyCard(pen) {
    return (
      <div key={pen.id} className="penalty-card penalty-card-own">
        <div className="penalty-card-header">
          <div className="penalty-employee">
            <div className="penalty-avatar">أنا</div>
            <div>
              <div className="penalty-employee-name">جزاء عليّ</div>
              <div className="penalty-employee-code">{pen.date}</div>
            </div>
          </div>
          {getStatusBadge(pen)}
        </div>

        <div className="penalty-details">
          <div className="penalty-detail-row">
            <span className="penalty-detail-label">نوع الجزاء:</span>
            <span className="penalty-detail-value">{pen.type}</span>
          </div>
          <div className="penalty-detail-row">
            <span className="penalty-detail-label">عدد الأيام:</span>
            <span className="penalty-detail-value">
              {formatPenaltyDays(pen.days)}
            </span>
          </div>
          <div className="penalty-detail-row">
            <span className="penalty-detail-label">الخصم:</span>
            <span className="penalty-detail-value">
              {pen.deductionLabel || "—"}
            </span>
          </div>
          <div className="penalty-detail-row">
            <span className="penalty-detail-label">السبب:</span>
            <span className="penalty-detail-value">{pen.reason}</span>
          </div>
          {pen.createdByName && (
            <div className="penalty-detail-row">
              <span className="penalty-detail-label">مُسجَّل بواسطة:</span>
              <span className="penalty-detail-value">{pen.createdByName}</span>
            </div>
          )}
          {pen.status === "appealed" && pen.appealDueAt ? (
            <AppealDeadlineClock dueAt={pen.appealDueAt} />
          ) : null}
        </div>

        <AttachmentList
          entityType="penalty"
          entityId={pen.id}
          showDelete={false}
          hideIfEmpty
          lazy
        />

        <div className="penalty-card-actions">
          {pen.status === "active" && (
            <button
              type="button"
              className="penalty-action-btn appeal"
              onClick={() => openAppealModal(pen.id)}
            >
              تقديم تظلم
            </button>
          )}
          {pen.status === "appealed" && (
            <span className="penalty-appeal-pending">تم التظلم</span>
          )}
        </div>
      </div>
    );
  }

  const reviewTarget = visiblePenalties.find(
    (p) => p.id === reviewModal.penaltyId,
  );

  return (
    <div className="penalty-manager" dir="rtl">
      {/* Header */}
      <div className="penalty-header">
        <div>
          <h2 className="penalty-title">إدارة الجزاءات</h2>
          <p className="penalty-subtitle">
            {listedCount} جزاء{isHrPenaltyView ? " خلال آخر شهرين" : ""} |{" "}
            {activeCount} ساري | {pendingHrCount} بانتظار HR | {appealedCount}{" "}
            معلق
          </p>
        </div>
        <button className="penalty-add-btn" onClick={openAddModal}>
          + إضافة جزاء
        </button>
      </div>

      {myOwnPenalties.length > 0 && (
        <div className="penalty-own-section">
          <div className="penalty-own-header">
            <h3 className="penalty-own-title">جزائاتي</h3>
            <p className="penalty-own-subtitle">
              يمكنك التظلم على الجزاءات السارية المسجّلة عليك
            </p>
          </div>

          <div className="penalty-own-month-block">
            <h4 className="penalty-own-month-title">
              {myOwnPenaltyGroups.currentMonthLabel}
            </h4>
            <div className="penalty-list">
              {myOwnPenaltyGroups.currentMonth.length > 0 ? (
                myOwnPenaltyGroups.currentMonth.map(renderOwnPenaltyCard)
              ) : (
                <p className="penalty-own-empty">لا توجد جزاءات في الشهر الحالي</p>
              )}
            </div>
          </div>

          {myOwnPenaltyGroups.otherMonths.length > 0 && (
            <div className="penalty-own-month-block">
              <h4 className="penalty-own-month-title">الأشهر السابقة</h4>
              {myOwnPenaltyGroups.otherMonths.map((group) => (
                <div key={group.monthKey} className="penalty-own-month-group">
                  <h5 className="penalty-own-month-subtitle">{group.label}</h5>
                  <div className="penalty-list">
                    {group.items.map(renderOwnPenaltyCard)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="penalty-filters">
        <button
          className={`filter-btn ${filter === "all" ? "active" : ""}`}
          onClick={() => setFilter("all")}
        >
          الكل
        </button>
        <button
          className={`filter-btn ${filter === "pending_hr" ? "active" : ""}`}
          onClick={() => setFilter("pending_hr")}
        >
          بانتظار HR
          {pendingHrCount > 0 && (
            <span className="penalty-filter-badge">
              {pendingHrCount > 99 ? "99+" : pendingHrCount}
            </span>
          )}
        </button>
        <button
          className={`filter-btn ${filter === "active" ? "active" : ""}`}
          onClick={() => setFilter("active")}
        >
          ساري
        </button>
        <button
          className={`filter-btn ${filter === "appealed" ? "active" : ""}`}
          onClick={() => setFilter("appealed")}
        >
          قيد التظلم
        </button>
        <button
          className={`filter-btn ${filter === "rejected" ? "active" : ""}`}
          onClick={() => setFilter("rejected")}
        >
          مرفوض
        </button>
      </div>

      {/* Search */}
      <div className="penalty-search">
        <input
          type="text"
          placeholder="بحث بالموظف أو نوع الجزاء..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="penalty-search-input"
        />
      </div>

      {/* Penalties List */}
      <div className="penalty-list">
        {filteredPenalties.length === 0 ? (
          <div className="penalty-empty">
            <p>🎉 لا توجد جزاءات حالياً</p>
          </div>
        ) : (
          filteredPenalties.map((pen) => {
            const { jobTitle, workplaceLabel } = getEmployeeMeta(
              pen.employeeId,
              pen,
            );
            return (
            <div key={pen.id} className="penalty-card">
              <div className="penalty-card-header">
                <div className="penalty-employee">
                  <div className="penalty-avatar">
                    {getEmployeeName(pen.employeeId)?.charAt(0)}
                  </div>
                  <div>
                    <div className="penalty-employee-name">
                      {getEmployeeName(pen.employeeId)}
                    </div>
                    <div className="penalty-employee-code">
                      {getEmployeeCode(pen.employeeId)}
                    </div>
                    {jobTitle ? (
                      <div className="penalty-employee-job">{jobTitle}</div>
                    ) : null}
                    {workplaceLabel ? (
                      <div className="penalty-employee-workplace">
                        {workplaceLabel}
                      </div>
                    ) : null}
                  </div>
                </div>
                {getStatusBadge(pen)}
              </div>

              <div className="penalty-details">
                <div className="penalty-detail-row">
                  <span className="penalty-detail-label">نوع الجزاء:</span>
                  <span className="penalty-detail-value">{pen.type}</span>
                </div>
                <div className="penalty-detail-row">
                  <span className="penalty-detail-label">التاريخ:</span>
                  <span className="penalty-detail-value">{pen.date}</span>
                </div>
                <div className="penalty-detail-row">
                  <span className="penalty-detail-label">عدد الأيام:</span>
                  <span className="penalty-detail-value">
                    {formatPenaltyDays(pen.days)}
                  </span>
                </div>
                <div className="penalty-detail-row">
                  <span className="penalty-detail-label">الخصم:</span>
                  <span className="penalty-detail-value">
                    {pen.deductionLabel || "—"}
                  </span>
                </div>
                <div className="penalty-detail-row">
                  <span className="penalty-detail-label">السبب:</span>
                  <span className="penalty-detail-value">{pen.reason}</span>
                </div>
                {pen.createdByName && (
                  <div className="penalty-detail-row">
                    <span className="penalty-detail-label">مُسجَّل بواسطة:</span>
                    <span className="penalty-detail-value">
                      {pen.createdByName}
                    </span>
                  </div>
                )}
                {pen.reviewNote && (
                  <div className="penalty-detail-row">
                    <span className="penalty-detail-label">ملاحظة HR:</span>
                    <span className="penalty-detail-value">{pen.reviewNote}</span>
                  </div>
                )}
                {pen.status === "appealed" && pen.appealDueAt ? (
                  <AppealDeadlineClock dueAt={pen.appealDueAt} />
                ) : null}
              </div>

              <AttachmentList
                entityType="penalty"
                entityId={pen.id}
                hideIfEmpty
                lazy
              />

              {canReviewPenalties && pen.status === "pending_hr" && (
                <div className="penalty-card-actions">
                  <button
                    type="button"
                    className="penalty-action-btn approve"
                    onClick={() => openReviewModal(pen.id, "approve")}
                  >
                    اعتماد
                  </button>
                  <button
                    type="button"
                    className="penalty-action-btn reject"
                    onClick={() => openReviewModal(pen.id, "reject")}
                  >
                    رفض
                  </button>
                </div>
              )}
            </div>
            );
          })
        )}
      </div>

      {/* Add Penalty Modal */}
      {showAddModal && (
        <div
          className="penalty-modal-overlay"
          onClick={() => setShowAddModal(false)}
        >
          <div className="penalty-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="penalty-modal-title">➕ إضافة جزاء جديد</h3>
            <p className="penalty-modal-subtitle">
              إضافة جزاء على موظف تحت إدارتك
            </p>

            <form onSubmit={handleAddPenalty}>
              <div className="penalty-modal-field">
                <label className="penalty-modal-label">الموظف *</label>
                <input
                  type="search"
                  className="penalty-modal-input"
                  placeholder="ابحث بالاسم أو الكود أو المسمى الوظيفي..."
                  value={employeeSearch}
                  onChange={(e) => setEmployeeSearch(e.target.value)}
                  autoComplete="off"
                />
                {managedEmployees.length === 0 ? (
                  <p className="penalty-modal-hint">
                    لا يوجد موظفون تحت إدارتك حالياً.
                  </p>
                ) : (
                  <div className="penalty-emp-pick-list">
                    {filteredManagedEmployees.length === 0 ? (
                      <p className="penalty-emp-pick-empty">
                        لا يوجد موظف مطابق للبحث
                      </p>
                    ) : (
                      filteredManagedEmployees.map((emp) => (
                        <button
                          type="button"
                          key={emp.id}
                          className={`penalty-emp-pick-item ${
                            String(newPenalty.employeeId) === String(emp.id)
                              ? "is-selected"
                              : ""
                          }`}
                          onClick={() => handleEmployeeSelect(emp.id)}
                        >
                          <strong>{emp.name}</strong>
                          <span>
                            {[emp.code, emp.jobTitle, emp.departmentName]
                              .filter(Boolean)
                              .join(" · ")}
                          </span>
                        </button>
                      ))
                    )}
                  </div>
                )}
                {!employeeSearch.trim() && managedEmployees.length > 60 && (
                  <p className="penalty-modal-hint-muted">
                    يظهر أول 60 موظفًا — اكتب الاسم أو الكود للوصول لأي موظف.
                  </p>
                )}
              </div>

              {selectedEmployee && (
                <div className="penalty-selected-employee">
                  <span>الموظف المختار: </span>
                  <strong>{selectedEmployee.name}</strong>
                  <span> ({selectedEmployee.code})</span>
                </div>
              )}

              <div className="penalty-modal-field">
                <label className="penalty-modal-label">نوع الجزاء *</label>
                <select
                  className="penalty-modal-select"
                  value={newPenalty.type}
                  onChange={(e) =>
                    setNewPenalty((prev) => ({ ...prev, type: e.target.value }))
                  }
                  required
                >
                  <option value="">اختر نوع الجزاء</option>
                  {penaltyTypes.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="penalty-modal-row">
                <div className="penalty-modal-field">
                  <label className="penalty-modal-label">تاريخ الجزاء *</label>
                  <input
                    type="date"
                    className="penalty-modal-input"
                    value={newPenalty.date}
                    onChange={(e) =>
                      setNewPenalty((prev) => ({
                        ...prev,
                        date: e.target.value,
                      }))
                    }
                    required
                  />
                </div>
                <div className="penalty-modal-field">
                  <label className="penalty-modal-label">
                    عدد الأيام{" "}
                    {newPenalty.type === "FINANCIAL_PENALTY" ? "*" : ""}
                  </label>
                  <input
                    type="number"
                    min="0.25"
                    step="0.25"
                    max="30"
                    className="penalty-modal-input"
                    placeholder="مثال: 0.5"
                    value={newPenalty.days}
                    onChange={(e) =>
                      setNewPenalty((prev) => ({
                        ...prev,
                        days: e.target.value,
                      }))
                    }
                    required={newPenalty.type === "FINANCIAL_PENALTY"}
                    disabled={newPenalty.type === "WARNING"}
                  />
                  {newPenalty.type === "FINANCIAL_PENALTY" && (
                    <div className="penalty-day-presets">
                      {DAY_PRESETS.map((preset) => (
                        <button
                          key={preset.value}
                          type="button"
                          className={`penalty-day-preset ${
                            Number(newPenalty.days) === preset.value
                              ? "active"
                              : ""
                          }`}
                          onClick={() =>
                            setNewPenalty((prev) => ({
                              ...prev,
                              days: String(preset.value),
                            }))
                          }
                        >
                          {preset.label}
                        </button>
                      ))}
                    </div>
                  )}
                  {newPenalty.type === "FINANCIAL_PENALTY" && (
                    <p className="penalty-modal-hint-muted">
                      يمكن اختيار ربع يوم أو نص يوم أو إدخال قيمة يدويًا (مضاعفات
                      0.25)
                    </p>
                  )}
                </div>
              </div>

              <div className="penalty-modal-field">
                <label className="penalty-modal-label">سبب الجزاء *</label>
                {specialPenalties.length > 0 && (
                  <select
                    className="penalty-modal-select"
                    value=""
                    onChange={(e) => {
                      const code = e.target.value;
                      const item = specialPenalties.find(
                        (row) => (row.code || row.labelAr) === code,
                      );
                      if (!item) return;
                      setNewPenalty((prev) => ({
                        ...prev,
                        reason: item.labelAr,
                        type: item.type || prev.type,
                        days:
                          item.type === "FINANCIAL_PENALTY" && item.days != null
                            ? String(item.days)
                            : prev.days,
                      }));
                    }}
                  >
                    <option value="">اختيار من لائحة الجزاءات الخاصة...</option>
                    {specialPenalties.map((item) => (
                      <option
                        key={item.code || item.labelAr}
                        value={item.code || item.labelAr}
                      >
                        {item.labelAr}
                        {item.days != null ? ` (${item.days} يوم)` : ""}
                      </option>
                    ))}
                  </select>
                )}
                <textarea
                  className="penalty-modal-textarea"
                  rows={3}
                  placeholder="اكتب سبب الجزاء بالتفصيل..."
                  value={newPenalty.reason}
                  onChange={(e) =>
                    setNewPenalty((prev) => ({
                      ...prev,
                      reason: e.target.value,
                    }))
                  }
                  required
                />
              </div>

              <AttachmentUploader
                entityType="penalty"
                files={penaltyFiles}
                onFilesChange={setPenaltyFiles}
                disabled={submitting}
                label="مرفق (اختياري)"
              />
              <p className="penalty-modal-hint-muted">
                يظهر للموظف مع تفاصيل الجزاء، ويمكن تركه فارغًا.
              </p>

              {error && <p className="penalty-modal-error">{error}</p>}

              <div className="penalty-modal-note">
                {createsDirectly
                  ? "⚠️ سيتم تفعيل الجزاء فورًا وإشعار الموظف."
                  : "⚠️ الجزاء سيُرسل أولاً لاعتماد الموارد البشرية. الموظف يُشعر فقط بعد الموافقة."}
              </div>

              <div className="penalty-modal-buttons">
                <button
                  type="button"
                  className="penalty-modal-btn secondary"
                  onClick={() => setShowAddModal(false)}
                >
                  إلغاء
                </button>
                <button
                  type="submit"
                  className="penalty-modal-btn primary"
                  disabled={submitting}
                >
                  {submitting
                    ? "جاري الحفظ..."
                    : createsDirectly
                      ? "إضافة الجزاء"
                      : "إرسال لاعتماد HR"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* HR Review Modal */}
      {reviewModal.open && (
        <div className="penalty-modal-overlay" onClick={closeReviewModal}>
          <div className="penalty-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="penalty-modal-title">
              {reviewModal.type === "approve" ? "اعتماد الجزاء" : "رفض الجزاء"}
            </h3>
            <p className="penalty-modal-subtitle">
              {reviewTarget
                ? `${getEmployeeName(reviewTarget.employeeId)} — ${reviewTarget.type}`
                : ""}
            </p>

            <div className="penalty-modal-field">
              <label className="penalty-modal-label">
                {reviewModal.type === "reject"
                  ? "سبب الرفض *"
                  : "ملاحظة (اختياري)"}
              </label>
              <textarea
                className="penalty-modal-textarea"
                rows={3}
                placeholder={
                  reviewModal.type === "reject"
                    ? "اكتب سبب رفض الجزاء..."
                    : "ملاحظة اختيارية..."
                }
                value={reviewModal.notes}
                onChange={(e) =>
                  setReviewModal((prev) => ({
                    ...prev,
                    notes: e.target.value,
                  }))
                }
                required={reviewModal.type === "reject"}
              />
            </div>

            {reviewTarget?.id && (
              <AttachmentList
                entityType="penalty"
                entityId={reviewTarget.id}
                showDelete={false}
                hideIfEmpty
              />
            )}

            {reviewError && <p className="penalty-modal-error">{reviewError}</p>}

            <div className="penalty-modal-note">
              {reviewModal.type === "approve"
                ? "بعد الاعتماد يصبح الجزاء ساريًا ويصل إشعار للموظف."
                : "عند الرفض لن يصل أي إشعار للموظف."}
            </div>

            <div className="penalty-modal-buttons">
              <button
                type="button"
                className="penalty-modal-btn secondary"
                onClick={closeReviewModal}
                disabled={reviewSubmitting}
              >
                إلغاء
              </button>
              <button
                type="button"
                className={`penalty-modal-btn ${
                  reviewModal.type === "approve" ? "primary" : "danger"
                }`}
                onClick={handleConfirmReview}
                disabled={reviewSubmitting}
              >
                {reviewSubmitting
                  ? "جاري التنفيذ..."
                  : reviewModal.type === "approve"
                    ? "تأكيد الاعتماد"
                    : "تأكيد الرفض"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Own-penalty appeal modal (HR / HR staff) */}
      {appealModal.open && (
        <div className="penalty-modal-overlay" onClick={closeAppealModal}>
          <div className="penalty-modal" onClick={(e) => e.stopPropagation()}>
            {!appealModal.submitted ? (
              <>
                <h3 className="penalty-modal-title">تقديم تظلم على الجزاء</h3>
                <p className="penalty-modal-subtitle">
                  {userRole === "hr"
                    ? "سيتم إرسال التظلم إلى المدير العام للمراجعة"
                    : "سيتم إرسال التظلم إلى المستوى الأعلى للمراجعة"}
                </p>
                <form onSubmit={handleAppealSubmit}>
                  <div className="penalty-modal-field">
                    <label className="penalty-modal-label">سبب التظلم *</label>
                    <textarea
                      className="penalty-modal-textarea"
                      rows={4}
                      placeholder="اكتب سبب تظلمك بالتفصيل..."
                      value={appealReason}
                      onChange={(e) => setAppealReason(e.target.value)}
                      required
                    />
                  </div>
                  {appealError && (
                    <p className="penalty-modal-error">{appealError}</p>
                  )}
                  <div className="penalty-modal-buttons">
                    <button
                      type="button"
                      className="penalty-modal-btn secondary"
                      onClick={closeAppealModal}
                      disabled={appealSubmitting}
                    >
                      إلغاء
                    </button>
                    <button
                      type="submit"
                      className="penalty-modal-btn primary"
                      disabled={appealSubmitting}
                    >
                      {appealSubmitting ? "جاري الإرسال..." : "إرسال التظلم"}
                    </button>
                  </div>
                </form>
              </>
            ) : (
              <div className="penalty-appeal-success">
                <p className="penalty-appeal-success-title">
                  ✅ تم إرسال التظلم بنجاح
                </p>
                <p className="penalty-appeal-success-subtitle">
                  سيتم إشعارك عند الرد على التظلم.
                </p>
                <button
                  type="button"
                  className="penalty-modal-btn secondary"
                  onClick={closeAppealModal}
                >
                  إغلاق
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default PenaltyManager;
